import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';

import { defaultFormat } from '../../assets/contants/dates-fns';
import { CoverImage } from './CoverImage';

import type { Project } from '../../app/entities/project';
import { tabPool } from '../../features/tabs/models/tabs-pool';
import { observer } from 'mobx-react-lite';
import { useResponsive } from '../../hooks/useResponsive';
import { ProjectMenu } from '../../features/assets/components/project-menu';
import { useCurrentMember } from '../../hooks/use-current-member';
import { Collaborators } from '@producer/ui-kit';
import { openModal } from '../../core/modal/open-modal';
import { ProjectCollaboratorModal } from '../../features/projects/modals/project-collaborator.modal';
import { Member } from '../../app/entities/member';

import styles from './ProjectItem.module.scss';
interface ProjectItemProps {
  project: Project;
}

export const ProjectItem: React.FC<ProjectItemProps> = observer(({ project }) => {
  const navigate = useNavigate();
  const { isDesktop } = useResponsive();

  const currentMember = useCurrentMember();

  const archivedByMember = useMemo(() => {
    if (!project?.archivedById) return null;
    return Member.getOne(project.archivedById);
  }, [project?.archivedById]);

  const handleOpenProject = (e: React.MouseEvent) => {
    const href = `/space/${project.spaceId}/projects/${project._id}`;

    tabPool.newTab({
      key: project._id,
      label: project.name,
      href,
    });

    if (!e.shiftKey) {
      navigate(href);
    }
  };

  return (
    <div
      className={styles['project-container']}
      onClick={handleOpenProject}
      data-archived={!!project.archived}
    >
      <div className={styles['cover']}>
        <CoverImage key={project.avatar} src={project.avatar || ''} title={project.name} />
        {isDesktop && ['owner', 'admin', 'creator'].includes(currentMember?.role || '') && (
          <div
            className={`${styles['bottom']} cover-collaborators`}
            onClick={(e) => e.stopPropagation()}
          >
            <Collaborators
              collaborators={project.collaborators}
              onClick={() =>
                openModal(ProjectCollaboratorModal, {
                  project,
                })
              }
            />
          </div>
        )}

        {isDesktop && (
          <div className={`${styles['project-menu-wrapper']} project-menu`}>
            <ProjectMenu project={project} />
          </div>
        )}
      </div>

      <div className={styles['infos']}>
        <h1 className={styles['project-title']}>{project.name}</h1>

        {project.archived && project.archivedAt ? (
          <div className={`${styles['info']} ${styles['archived-info']}`}>
            Archived {format(new Date(project.archivedAt), defaultFormat)}
            {archivedByMember && ` by ${archivedByMember.fullName}`}
          </div>
        ) : (
          <>
            <div className={styles['info']}>{`Last Updated: ${
              project.updatedAt ? format(new Date(project.updatedAt), defaultFormat) : '-'
            }`}</div>
            <div className={styles['info']}>{`Project Progress: ${project.progress}%`}</div>
          </>
        )}
      </div>
    </div>
  );
});
